import type { TwinReadiness, TwinReadinessStep, TwinReadinessStepStatus } from '../types';

/**
 * Twin readiness — the single requirement-based model behind both the profile
 * header/index readiness states and the setup checklist. Completeness percent
 * and the descriptive state are derived from the SAME step list, so they can
 * never contradict each other.
 *
 * Steps mirror the twin-readiness workflow:
 *   1. Confirm store identity
 *   2. Upload or verify the floorplan
 *   3. Define aisles and zones
 *   4. Import images and walkthroughs
 *   5. Review suggested spatial mappings
 *   6. Resolve unmapped evidence
 *   7. Publish or mark the twin ready
 */
export interface TwinReadinessInput {
  /** Store record has identity basics (name, address, retailer) */
  hasStoreIdentity: boolean;
  hasFloorplanImage: boolean;
  aisleCount: number;
  zoneCount: number;
  /** Walkthroughs + images (campaign evidence + reference imagery) */
  evidenceCount: number;
  /** Evidence with location hints awaiting a canonical aisle/zone link */
  pendingSuggestionCount: number;
  /** Evidence with no spatial link at all */
  unmappedEvidenceCount: number;
  twinPublishedAt: string | null;
}

interface StepDefinition {
  id: string;
  label: string;
  description: string;
  evaluate: (input: TwinReadinessInput) => { status: TwinReadinessStepStatus; detail: string };
}

const STEP_DEFINITIONS: StepDefinition[] = [
  {
    id: 'identity',
    label: 'Confirm store identity',
    description: 'Store record has a name, address and retailer.',
    evaluate: (input) => input.hasStoreIdentity
      ? { status: 'complete', detail: 'Store record verified' }
      : { status: 'action_needed', detail: 'Store record is missing identity details' },
  },
  {
    id: 'floorplan',
    label: 'Upload or verify the floorplan',
    description: 'A current floorplan image is the spatial anchor of the twin.',
    evaluate: (input) => input.hasFloorplanImage
      ? { status: 'complete', detail: 'Floorplan image on file' }
      : { status: 'action_needed', detail: 'No floorplan image uploaded' },
  },
  {
    id: 'aisles',
    label: 'Define store areas',
    description: 'Draw aisles and zones on the floorplan.',
    evaluate: (input) => {
      if (input.aisleCount > 0) {
        return {
          status: 'complete',
          detail: input.zoneCount > 0
            ? `${input.aisleCount} aisle${input.aisleCount === 1 ? '' : 's'}, ${input.zoneCount} zone${input.zoneCount === 1 ? '' : 's'} defined`
            : `${input.aisleCount} aisle${input.aisleCount === 1 ? '' : 's'} defined`,
        };
      }
      return {
        status: input.hasFloorplanImage ? 'action_needed' : 'blocked',
        detail: input.hasFloorplanImage ? 'No aisles defined on the floorplan' : 'Upload a floorplan first',
      };
    },
  },
  {
    id: 'evidence',
    label: 'Import images and walkthroughs',
    description: 'Baseline imagery, campaign proof and walk-through videos.',
    evaluate: (input) => input.evidenceCount > 0
      ? { status: 'complete', detail: `${input.evidenceCount} item${input.evidenceCount === 1 ? '' : 's'} imported` }
      : { status: 'action_needed', detail: 'No images or walkthroughs yet' },
  },
  {
    id: 'mappings',
    label: 'Review suggested locations',
    description: 'Confirm photos and videos suggested by posting instructions.',
    evaluate: (input) => {
      if (input.evidenceCount === 0) {
        return { status: 'blocked', detail: 'Import evidence first' };
      }
      return input.pendingSuggestionCount === 0
        ? { status: 'complete', detail: 'No suggestions waiting for review' }
        : {
            status: 'action_needed',
            detail: `${input.pendingSuggestionCount} suggested location${input.pendingSuggestionCount === 1 ? '' : 's'} to review`,
          };
    },
  },
  {
    id: 'unmapped',
    label: 'Place remaining photos',
    description: 'Every photo and video placed on a store area.',
    evaluate: (input) => {
      if (input.evidenceCount === 0) {
        return { status: 'blocked', detail: 'Import evidence first' };
      }
      return input.unmappedEvidenceCount === 0
        ? { status: 'complete', detail: 'All evidence is placed on the map' }
        : {
            status: 'action_needed',
            detail: `${input.unmappedEvidenceCount} item${input.unmappedEvidenceCount === 1 ? '' : 's'} still need placement`,
          };
    },
  },
  {
    id: 'publish',
    label: 'Make available to retailers',
    description: 'Sign off that retailers can explore this store.',
    evaluate: (input) => input.twinPublishedAt
      ? { status: 'complete', detail: `Available since ${input.twinPublishedAt.slice(0, 10)}` }
      : { status: 'action_needed', detail: 'Not yet available to retailers' },
  },
];

export const TWIN_READINESS_STEP_COUNT = STEP_DEFINITIONS.length;

export function deriveTwinReadiness(input: TwinReadinessInput): TwinReadiness {
  const steps: TwinReadinessStep[] = STEP_DEFINITIONS.map((definition) => {
    const { status, detail } = definition.evaluate(input);
    return { id: definition.id, label: definition.label, description: definition.description, status, detail };
  });

  const completedCount = steps.filter((step) => step.status === 'complete').length;
  const totalCount = steps.length;
  const percent = Math.round((completedCount / totalCount) * 100);

  const prePublishSteps = steps.filter((step) => step.id !== 'publish');
  const prePublishComplete = prePublishSteps.every((step) => step.status === 'complete');
  const anyStarted = steps.some((step) => step.status === 'complete');

  const state: TwinReadiness['state'] = input.twinPublishedAt
    ? 'published'
    : prePublishComplete
      ? 'ready_to_publish'
      : anyStarted
        ? 'in_progress'
        : 'not_started';

  const nextStep = input.twinPublishedAt
    ? null
    : (steps.find((step) => step.status === 'action_needed')
      ?? steps.find((step) => step.status === 'blocked')
      ?? null);

  return {
    steps,
    completedCount,
    totalCount,
    percent,
    state,
    nextAction: nextStep ? { stepId: nextStep.id, label: nextStep.label } : null,
  };
}

export const TWIN_READINESS_STATE_LABELS: Record<TwinReadiness['state'], string> = {
  not_started: 'Not started',
  in_progress: 'In progress',
  ready_to_publish: 'Ready for retailers',
  published: 'Available to retailers',
};
